// src/prototype/pages/EAPrintableUseCaseProfilePage.tsx
// ============================================================
// Standalone print-ready Use Case Profile page.
// Route: /use-case/print (outside AppShell, no sidebar)
//
// Pattern: Identical to EAPrintableReportPage.tsx (PIR)
// EAUseCaseBriefPage.tsx is monolithic — no exportable section
// components exist. Per agreed approach, profile data logic is
// replicated inline from EAUseCaseBriefPage. All section
// content mirrors EAUseCaseBriefPage exactly.
//
// EAUseCaseBriefPage.tsx was NOT modified.
// No existing in-app UI was changed.
//
// QI-3 Improvements (March 2026):
//   1. Interactive controls (buttons, selects) hidden in print
//   2. Atomic block protection via ucp-atomic utility class
//   3. CSS selectors protect rounded-* and border-radius blocks
//   4. Grid/flex child protection for metric rows, panels, etc.
//   5. h2/h3/h4 break-after: avoid
//   6. Orphan/widow control on paragraphs
//   7. SectionHeader upgraded with bottom divider line
//   8. InfoCard tagged ucp-atomic
//   9. ucp-cover hardened with both page-break and break syntax
//
// QI-4 Improvements (March 2026) — Services Pivot:
//  10. Standalone cover page REMOVED; replaced with insight-first page 1
//  11. Page 1: recommendation banner, 6 KPI cards, executive narrative,
//      3 reasons to advance, sponsor recommendation callout
//  12. ucp-page1 class replaces ucp-cover for page break control
//
// QI-5 Improvements (March 2026) — Decision Artifact Format:
//  13. Duplicate "Executive Summary" section REMOVED (page 1 supersedes)
//  14. New page 2: "Decision Breakdown & Supporting Analysis" navigation
//  15. Document structure: Page 1 → Page 2 (Navigation) → Pages 3+ (Detail)
//
// QI-6 Improvements (March 2026) — Executive Density Pass:
//  16. Page 1 tightened — reduced spacing/padding to ensure single-page fit
//  17. Page 2 replaced — "How to Use This Decision Profile" with role-based
//      navigation (CFO, CDO, COO) + key metrics summary grid
//  18. Detail sections converted to structured bullet format (~25% word reduction)
//  19. Financial narrative, feasibility panels, and risk blocks condensed
// ============================================================

import React, { useMemo } from 'react';
import { Printer, ArrowLeft } from 'lucide-react';
import { effortTierForBadge } from '../config/opportunityBadge';
import { roiForUseCase } from '../roi/useCaseRoi';
import { OPP_TO_UC, scoreForUseCase } from '../data/adapters/stage300FeaturedContent';
import { useNavigate, useLocation } from 'react-router-dom';
import { readExportedUseCaseId, resolveExportReturn } from '../utils/exportReturn';

// ── Data sources ────────────────────────────────────────────
import { DEMO_CLIENT } from '../config/demoClient';
import { DEMO_OPPORTUNITIES } from '../data/demoOpportunities';

// ── Helpers ─────────────────────────────────────────────────
const formatDate = (): string =>
  new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

// The ranked-set size, NOT the 174-use-case reference library — ranking is computed only over
// DEMO_OPPORTUNITIES, so a rank claim needs this denominator to match what was actually ranked.
const TOTAL_PORTFOLIO_SIZE = DEMO_OPPORTUNITIES.length;

// ── Minimal types ────────────────────────────────────────────
interface RiskItem {
  name: string;
  severity: 'Critical' | 'High' | 'Medium' | 'Low';
  likelihood: 'Very High' | 'High' | 'Medium' | 'Low' | 'Very Low';
  description: string;
  mitigation: string;
  owner: string;
}

interface MilestoneItem {
  label: string;
  months_from_start: number;
  value_note: string;
  expected_outcome: string;
  status: 'completed' | 'in-progress' | 'planned';
  owner: string;
}

interface ProfileROI {
  annual_benefit_m: number;
  implementation_cost_m: number;
  payback_months: number;
  npv_3yr_m: number;
  confidence: number;
}

interface ProfileData {
  use_case_id: string;
  title: string;
  category: string;
  priority_label: string;
  recommendation: 'PROCEED' | 'EVALUATE' | 'DEFER';
  executive_summary: string;
  rationale: string;
  use_case_description: string;
  // QI-6: pipe-separated bullet strings for detail sections
  strategic_narrative: string;
  strategic_context: string;
  organizational_impact: string;
  operational_impact: string;
  clinical_impact: string;
  stakeholder_alignment: string;
  roi_range: string;
  time_to_roi: string;
  implementation_effort: 'Low' | 'Medium' | 'High';
  value_score: number;
  implementation_score: number;
  total_score: number;
  rank: number;
  percentile: number;
  roi: ProfileROI;
  roi_narrative: string;
  roi_assumptions: { label: string; value: string }[];
  feasibility: {
    complexity_summary: string;
    technology_readiness: string;
    data_availability: string;
    organizational_readiness: string;
    integration_complexity: string;
    operational_dependencies: string;
    change_management: string;
  };
  risks: RiskItem[];
  milestones: MilestoneItem[];
  next_steps: string[];
  actions: {
    immediate: { title: string; description: string; owner: string }[];
    near_term:  { title: string; description: string; owner: string }[];
    strategic:  { title: string; description: string; owner: string }[];
  };
}

// ── Risk data — QI-6: descriptions condensed ~30% ───────────
const RISK_DATA: Record<'Low' | 'Medium' | 'High', { names: string[]; items: Omit<RiskItem, 'name'>[] }> = {
  Low: {
    names: ['Data Quality Risk', 'User Adoption Risk', 'Vendor Dependency Risk'],
    items: [
      {
        severity: 'Medium', likelihood: 'Low',
        description: 'Data quality inconsistencies may require remediation before deployment. Completeness rates of 85–92% are within range, but targeted cleansing is needed in two source domains.',
        mitigation: 'Formal data quality assessment in Phase 1. Minimum quality thresholds defined as a go/no-go criterion before build begins.',
        owner: 'Chief Data Officer',
      },
      {
        severity: 'Medium', likelihood: 'Medium',
        description: 'Adoption may lag without dedicated change management. Comparable health systems achieved 60–70% utilization within 90 days when structured training programs were in place.',
        mitigation: 'Role-specific training curriculum with clinical champions in each department. Feedback loop established for early adoption barriers.',
        owner: 'VP of Analytics',
      },
      {
        severity: 'Low', likelihood: 'Low',
        description: 'Vendor integration timelines may shift based on third-party release schedules. Commitments are verbal; contractual SLAs not yet established.',
        mitigation: 'Parallel development tracks. Contractually bind SLAs during procurement with penalty provisions for schedule slippage.',
        owner: 'CTO / IT',
      },
    ],
  },
  Medium: {
    names: ['Integration Complexity Risk', 'Data Governance Risk', 'Adoption & Sustainment Risk', 'Regulatory Change Risk'],
    items: [
      {
        severity: 'High', likelihood: 'Medium',
        description: 'EHR integration may extend the timeline by 4–8 weeks. Primary vendor has indicated a 6–8 week API provisioning lead time not yet reflected in the project schedule.',
        mitigation: 'Formal EHR vendor discovery in the first two weeks. Scope integration requirements precisely and model timeline impact before finalizing the project plan.',
        owner: 'CTO / IT',
      },
      {
        severity: 'Medium', likelihood: 'Medium',
        description: 'Governance gaps may delay access to required clinical datasets. Current frameworks do not explicitly address cross-departmental data sharing requirements.',
        mitigation: 'Governance review and IRB assessment initiated in parallel with technical build. Privacy Officer and Compliance engaged in Week 1.',
        owner: 'Chief Data Officer',
      },
      {
        severity: 'Medium', likelihood: 'High',
        description: 'Sustained adoption beyond go-live requires ongoing support and monitoring. Without infrastructure, adoption typically declines 30–40% within six months of launch.',
        mitigation: 'Embedded analytics support function with defined escalation paths. Adoption metrics built into governance framework and reviewed quarterly.',
        owner: 'VP of Analytics',
      },
      {
        severity: 'Low', likelihood: 'Medium',
        description: 'Evolving regulatory guidance may require model recalibration or workflow adjustments post-launch, adding unplanned maintenance cost.',
        mitigation: 'Quarterly regulatory landscape review with Legal and Compliance. Model refresh cycles built into the annual roadmap.',
        owner: 'Compliance / Legal',
      },
    ],
  },
  High: {
    names: ['Systems Integration Risk', 'Change Management Risk', 'Compliance & Regulatory Risk', 'Data Readiness Risk', 'Vendor Scalability Risk'],
    items: [
      {
        severity: 'Critical', likelihood: 'Medium',
        description: 'Cross-system integration spans four source systems with divergent data models, requiring a custom integration layer with significant complexity and failure risk.',
        mitigation: 'Senior integration architect in Phase 1. Phased integration approach with strict data contract standards across all upstream systems before build begins.',
        owner: 'CTO / IT',
      },
      {
        severity: 'High', likelihood: 'High',
        description: 'Workflow changes affect clinical and operational staff across departments. Without executive commitment to change management, adoption failure is likely.',
        mitigation: 'Dedicated executive sponsor with resource authority. Formal change impact assessment and minimum 12 weeks of structured change management support.',
        owner: 'COO / CMO',
      },
      {
        severity: 'High', likelihood: 'Medium',
        description: 'Regulatory requirements may introduce mandatory review gates delaying the timeline 4–12 weeks. Legal exposure is elevated without proactive engagement.',
        mitigation: 'Compliance review gate in Stage 2. Legal counsel engaged in discovery to map all regulations and required approval pathways.',
        owner: 'Compliance / Legal',
      },
      {
        severity: 'Medium', likelihood: 'Medium',
        description: 'Data quality remediation across source systems is a hard prerequisite. Completeness gaps exist in 3 of 6 source domains; estimated 6–8 weeks remediation effort.',
        mitigation: 'Dedicated data engineering resources for pre-implementation data prep. Data quality SLAs defined per source system before moving to build.',
        owner: 'Chief Data Officer',
      },
      {
        severity: 'Medium', likelihood: 'Low',
        description: 'Pilot results from early-adopter systems may not generalize at enterprise scale for higher patient volumes or more complex payer mixes.',
        mitigation: 'Formal vendor evaluation with reference checks from comparable health systems. Performance guarantees required contractually.',
        owner: 'VP of Analytics',
      },
    ],
  },
};

const buildMilestones = (title: string): MilestoneItem[] => [
  { label: 'Discovery & Requirements',     months_from_start: 1,  value_note: `Finalize data requirements, conduct sponsor interviews, and align on success criteria for ${title}.`, expected_outcome: 'Signed-off requirements document, data access plan, and steering committee established.', status: 'planned', owner: '📊' },
  { label: 'Data Infrastructure & Access', months_from_start: 2,  value_note: 'Establish data pipelines, secure governance approvals, and validate QA framework against source data.', expected_outcome: 'Data pipelines live, governance approvals obtained, QA baseline established.', status: 'planned', owner: '🔧' },
  { label: 'Model and System Build',       months_from_start: 4,  value_note: 'Core analytics model developed, tested against historical data, and reviewed with clinical leaders.', expected_outcome: 'Confirmed model achieving target accuracy thresholds. Sponsor sign-off on outputs.', status: 'planned', owner: '🤖' },
  { label: 'Initial Launch',                 months_from_start: 6,  value_note: 'Controlled launch with a defined cohort. Measure against baseline KPIs and capture end-user adoption feedback.', expected_outcome: 'Early KPI results vs. baseline. Adoption rate ≥ 60%. Issue log documented.', status: 'planned', owner: '🚀' },
  { label: 'Evaluation & Iteration',       months_from_start: 8,  value_note: 'Analyze pilot outcomes, refine model based on clinical feedback, and finalize enterprise scale-up plan.', expected_outcome: 'Refined model approved. Scale-up plan and resource allocation confirmed.', status: 'planned', owner: '📈' },
  { label: 'Full Deployment',              months_from_start: 12, value_note: 'Enterprise-wide rollout with support model in place. ROI tracking dashboards live for Finance and Operations.', expected_outcome: 'All target departments live. ROI tracking active. Support model operational.', status: 'planned', owner: '✅' },
];

// ── Build profile from opportunity ──────────────────────────
// QI-6: Detail-section strings use '|' as bullet separator
// Exported so the ROI reconciliation tests can assert this page's financials
// against the engine without rendering the whole printable document.
export function buildProfile(opp: any, rank: number): ProfileData {
  // Effort tier and scores come from the authored root via acme-demo-v1 (the same source the
  // storefront ranks by and the decision brief reads); the rank-derived approximation is kept
  // only as the fallback for non-featured fillers.
  const effort      = effortTierForBadge({ id: opp.id, implementationEffort: opp.implementationEffort });
  const effortBonus = { Low: 12, Medium: 0, High: -10 }[effort];
  const rootScore   = scoreForUseCase(OPP_TO_UC[opp.id]);
  const valueScore  = rootScore ? Math.round(rootScore.value) : Math.round(Math.max(52, Math.min(93, 89 - rank * 1.4)));
  const implScore   = rootScore ? Math.round(rootScore.impl)  : Math.round(Math.max(42, Math.min(90, 74 + effortBonus - rank * 0.7)));
  const totalScore  = rootScore ? Math.round(rootScore.composite) : Math.round(valueScore * 0.55 + implScore * 0.45);
  // Genuine percentile against the ranked set, not an authored curve — see decisionBrief.ts.
  const percentile  = ((TOTAL_PORTFOLIO_SIZE - rank) / TOTAL_PORTFOLIO_SIZE) * 100;
  // ── FINANCIALS COME FROM THE ROI ENGINE ────────────────────────────────
  // This page used to run its own fourth ROI model: cost as a multiple of annual
  // benefit by effort tier ({Low:0.32,...}), payback from that, NPV as
  // (annual*3 - cost)*0.87, and confidence INVERTED from effort
  // ({Low:84,Medium:74,High:64}) — which is where the 84% / 4-month pairing came
  // from. None of it agreed with the ROI Model page, the Command Center, or the
  // approval PDFs, and confidence keyed off implementation effort rather than off
  // the quality of the model's inputs.
  const engine      = roiForUseCase(opp.id).scenarios.base;
  const annualM     = parseFloat((engine.annualValueMid / 1e6).toFixed(2));
  const costM       = parseFloat((engine.implementationCost / 1e6).toFixed(2));
  const payback     = engine.paybackMonths ?? 0;
  const npv3M       = parseFloat((engine.netValue3Yr / 1e6).toFixed(2));
  const confidence  = engine.confidencePct;
  const roiLow      = engine.annualValueLow / 1e6;
  const roiHigh     = engine.annualValueHigh / 1e6;
  const recommendation: 'PROCEED' | 'EVALUATE' | 'DEFER' =
    totalScore >= 72 ? 'PROCEED' : totalScore >= 55 ? 'EVALUATE' : 'DEFER';
  const priorityLabel  = rank <= 5 ? 'Tier 1 Priority' : rank <= 15 ? 'Strategic Fit' : 'Future Consideration';
  const riskDef        = RISK_DATA[effort];
  const risks          = riskDef.names.map((name, i) => ({ name, ...riskDef.items[i] }));
  const roiRange       = `$${roiLow.toFixed(1)}M – $${roiHigh.toFixed(1)}M`;
  const timeToRoi      = opp.timeToValue || '6–12 months';
  const readinessLevel = { Low: 'High', Medium: 'Moderate', High: 'Low' }[effort];

  return {
    use_case_id: opp.id,
    title: opp.title,
    category: opp.category,
    priority_label: priorityLabel,
    recommendation,

    // ── Page 1 prose (unchanged) ──────────────────────────
    executive_summary:
      `${opp.title} is a ${priorityLabel.toLowerCase()} analytics initiative within the ${opp.category} domain. ` +
      `Projected annual benefit of ${roiRange} with a ${timeToRoi} time-to-value trajectory positions this initiative ` +
      `at rank #${rank} of ${TOTAL_PORTFOLIO_SIZE} — ${Math.round(percentile)}th percentile of the full portfolio. ` +
      `The composite score of ${totalScore}/100 supports a recommendation to ` +
      (recommendation === 'PROCEED'
        ? 'advance to planning. The initiative meets key investment criteria and is recommended for leadership review and phased activation.'
        : recommendation === 'EVALUATE'
        ? 'conduct further feasibility evaluation prior to full commitment. Additional discovery will sharpen the ROI case.'
        : 'defer to the next strategic planning cycle pending organizational readiness improvements.'),

    rationale:
      `This initiative demonstrates ${valueScore >= 75 ? 'strong' : 'moderate'} business value (${valueScore}/100) ` +
      `and ${implScore >= 65 ? 'favorable' : 'manageable'} implementation feasibility (${implScore}/100). ` +
      (recommendation === 'PROCEED'
        ? 'ROI fundamentals are structured for CFO-level review, organizational readiness supports a phased launch, and strategic alignment is indicated across CDO and operational leadership. The initiative is recommended for resource planning and governance review.'
        : recommendation === 'EVALUATE'
        ? 'Key ROI assumptions require validation through a structured feasibility phase. Additional discovery on data readiness and integration complexity is recommended before committing full implementation resources.'
        : 'This initiative should be revisited in the next planning cycle as organizational readiness and strategic priorities evolve.'),

    // ── Description prose ─────────────────────────────────
    use_case_description:
      opp.description ||
      `${opp.title} enables the health system to leverage analytics to drive measurable improvement ` +
      `in ${opp.category.toLowerCase()} outcomes. A structured data pipeline ingests clinical, operational, ` +
      `and financial signals from existing source systems and transforms them into actionable decision support.`,

    // ── QI-6: Pipe-separated bullet strings ───────────────
    strategic_narrative:
      `${opp.title} is a core ${opp.category.toLowerCase()} analytics initiative with direct alignment to enterprise data strategy.` +
      `|Provides a structured pathway from data availability to clinical and operational impact — closing the insight-to-value gap.` +
      `|Builds reusable capability that compounds across future use cases in the portfolio.`,

    strategic_context:
      `${opp.category} is a top-priority investment domain for health systems navigating the value-based care transition.` +
      `|Peer organizations report 12–18% quality metric improvement within 18 months of analytics deployment in this domain.` +
      `|Early activation positions the health system ahead of peer adoption curves while building durable infrastructure.`,

    organizational_impact:
      `Establishes a repeatable analytics pattern for the ${opp.category.toLowerCase()} domain — extendable to related use cases.` +
      `|Data infrastructure built during implementation will support 3–5 downstream portfolio use cases.` +
      `|Demonstrated ROI delivery measurably strengthens board and executive confidence in the data function.`,

    operational_impact:
      `15–25% reduction in targeted workflow effort through real-time data availability.` +
      `|Early efficiency gains achievable within 6 months; full run-rate impact by month 12.`,

    clinical_impact:
      `Improved decision support at the point of care; reduced unwarranted variation across care delivery.` +
      `|8–15% quality metric improvement projected within 12 months of full deployment.`,

    stakeholder_alignment:
      `CDO and VP of Analytics identified as primary sponsors.` +
      `|CMO and COO alignment recommended for workflow integration and change management.` +
      `|ROI methodology structured for CFO-level review prior to formal commitment.`,

    roi_range: roiRange,
    time_to_roi: timeToRoi,
    implementation_effort: effort,
    value_score: valueScore,
    implementation_score: implScore,
    total_score: totalScore,
    rank,
    percentile: Math.round(percentile),
    roi: { annual_benefit_m: annualM, implementation_cost_m: costM, payback_months: payback, npv_3yr_m: npv3M, confidence },

    // QI-6: Condensed financial narrative
    roi_narrative:
      `Financial projections use industry benchmarks and peer health system performance data. ` +
      `The ROI range reflects scenario-based outcomes (conservative to aggressive); base-case figures are structured for finance review. ` +
      `The ${effort.toLowerCase()} implementation complexity yields a ${payback}-month payback on $${annualM.toFixed(1)}M annual benefit ` +
      `against $${costM.toFixed(1)}M year-1 implementation plus $${(engine.operatingCostPerYear / 1e6).toFixed(1)}M/yr operating ` +
      `($${(engine.totalCost3Yr / 1e6).toFixed(1)}M total 3-year cost). Three-year net value: $${npv3M.toFixed(1)}M at ${confidence}% model confidence.`,

    roi_assumptions: [
      { label: 'Annual Patient Volume',    value: '85,000 encounters'                          },
      { label: 'Addressable Opportunity',  value: `${Math.min(28, Math.max(12, Math.round(annualM * 1.2)))}% of eligible encounter volume` },
      { label: 'Benefit Realization Rate', value: `${Math.round(engine.year1RampFraction * 100)}% in Y1 → 100% in Y2` },
      { label: 'Implementation Timeline',  value: '12 months to full deployment'               },
      { label: 'Discount Rate (NPV)',       value: '8% (standard hurdle rate)'                 },
      { label: 'Inflation Adjustment',     value: '3% annually for ongoing costs'              },
      { label: 'Comparator Benchmarks',    value: '12 peer health systems (AVIA, KLAS)'        },
    ],

    // QI-6: Condensed feasibility sub-fields
    feasibility: {
      complexity_summary:
        effort === 'Low'
          ? `Low complexity — implementation score ${implScore}/100. Leverages existing infrastructure with minimal new integration requirements.`
          : effort === 'Medium'
          ? `Moderate complexity — implementation score ${implScore}/100. Requires integration work and organizational coordination.`
          : `High complexity — implementation score ${implScore}/100. Multi-system integration with significant change management scope.`,
      technology_readiness:
        effort === 'Low'
          ? `High readiness — core infrastructure in place; minimal new tooling required.`
          : effort === 'Medium'
          ? `Moderate readiness — components available; configuration and integration work needed.`
          : `Significant investment required to extend infrastructure for required integration patterns.`,
      data_availability:
        effort === 'Low'
          ? `Strong availability — primary sources accessible via existing pipelines; completeness above 90%.`
          : effort === 'Medium'
          ? `Moderate availability — core sources accessible; completeness 80–92%; additional preparation required.`
          : `Partial availability — multiple systems require integration work and completeness remediation.`,
      organizational_readiness:
        effort === 'Low'
          ? `High readiness — stakeholders experienced with analytics tools and motivated for data-driven decisions.`
          : effort === 'Medium'
          ? `Key stakeholders engaged; workflow changes will require structured change management support.`
          : `Change management is the primary risk factor; a formal program is a prerequisite for success.`,
      integration_complexity:
        effort === 'Low'
          ? `Straightforward — 1–2 source systems via provisioned APIs in the current environment.`
          : effort === 'Medium'
          ? `Moderate — 3–4 systems; API and SQL extraction mix; est. 6–8 weeks development effort.`
          : `Complex multi-system — 5+ source systems with divergent data models; primary technical complexity driver.`,
      operational_dependencies:
        `Executive sponsor, IT resource (${effort === 'Low' ? '0.5' : effort === 'Medium' ? '1.0' : '2.0'} FTE / ` +
        `${effort === 'Low' ? '4' : effort === 'Medium' ? '8' : '16'} weeks), data governance approval, clinical workflow coordination for UAT.`,
      change_management:
        effort === 'Low'
          ? `Standard — dept-level training (2 hrs/team), designated power users, 30-day post-go-live hypercare.`
          : effort === 'Medium'
          ? `Moderate — role-based training (4–8 hrs/persona), clinical champions, 30/60/90-day feedback cycles.`
          : `Substantial — dedicated CM lead required; full impact assessment; 5+ role-specific training tracks.`,
    },

    risks,
    milestones: buildMilestones(opp.title),
    next_steps: [
      'Assign executive sponsor and constitute a cross-functional steering committee with CDO, IT, Finance, and Operations representation.',
      'Commission a data readiness assessment and formal integration scoping exercise (target completion: 2 weeks).',
      'Issue RFI to qualified implementation partners; shortlist for structured evaluation by end of quarter.',
    ],
    actions: {
      immediate: [
        { title: 'Designate Executive Sponsor',         description: 'Formally assign a C-suite sponsor with budget authority. Document sponsor responsibilities and escalation protocols. Schedule monthly steering committee cadence with defined decision rights.', owner: 'CDO / COO'          },
        { title: 'Constitute Steering Committee',        description: 'Establish a cross-functional steering committee with representatives from CDO office, IT, Finance, Operations, and Clinical leadership. Define meeting cadence and escalation paths.',               owner: 'CDO'               },
        { title: 'Commission Data Readiness Assessment', description: 'Engage data engineering team to profile all required source systems. Assess completeness rates, data quality issues, and integration complexity. Deliver findings and remediation plan within 2 weeks.', owner: 'VP of Analytics'  },
      ],
      near_term: [
        { title: 'Complete Integration Scoping',         description: 'Engage EHR vendor and IT to formally scope integration requirements. Document API provisioning timelines, data model mapping, and estimated engineering effort.',                                         owner: 'CTO / IT'          },
        { title: 'Issue RFI to Implementation Partners', description: 'Distribute RFI to 3–5 qualified implementation vendors with demonstrated healthcare analytics experience. Shortlist within 4 weeks.',                                                                    owner: 'VP of Analytics'   },
        { title: 'Initiate Governance Review',           description: 'Engage Privacy Officer and Compliance team to map required data governance approvals, IRB considerations, and DUA requirements for all data domains.',                                                    owner: 'Chief Data Officer' },
      ],
      strategic: [
        { title: 'Develop Portfolio Scaling Plan',       description: 'Following successful deployment, document the analytics pattern and infrastructure components reusable for downstream portfolio use cases. Identify top 3 candidates for accelerated deployment.',         owner: 'CDO / VP of Analytics' },
        { title: 'Establish ROI Measurement Framework',  description: 'Define measurement methodology, baseline metrics, and reporting cadence for ongoing ROI tracking. Build a Finance-approved dashboard for quarterly executive reporting.',                                 owner: 'Finance / CDO'     },
        { title: 'Plan Board-Level Reporting Integration', description: 'Design executive reporting artifacts suitable for board consumption. Target first board presentation for Q3 post-deployment to demonstrate initiative ROI.',                                           owner: 'CDO'               },
      ],
    },
  };
}

// ── QI-6: Bullet renderer for pipe-separated strings ────────
const toBullets = (text: string) => (
  <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
    {text.split('|').map((pt, i, arr) => (
      <li key={i} style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: i < arr.length - 1 ? '5px' : 0 }}>
        <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#FF6E4C', flexShrink: 0, marginTop: '7px' }} />
        <span style={{ fontSize: '12px', lineHeight: 1.65, color: '#374151' }}>{pt.trim()}</span>
      </li>
    ))}
  </ul>
);

// ── QI-3: SectionHeader with bottom divider ──────────────────
const SectionHeader: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <div style={{ marginBottom: '24px', marginTop: '4px', paddingBottom: '16px', borderBottom: '1px solid #e5e7eb' }}>
    <h2 style={{ fontSize: '20px', fontWeight: 700, color: '#18365E', margin: 0, fontFamily: 'Montserrat, sans-serif' }}>
      {title}
    </h2>
    {subtitle && <p style={{ fontSize: '13px', color: '#717182', marginTop: '4px', marginBottom: 0, fontFamily: 'Montserrat, sans-serif' }}>{subtitle}</p>}
    <div style={{ width: '48px', height: '3px', backgroundColor: '#FF6E4C', borderRadius: '2px', marginTop: '12px' }} />
  </div>
);

// ── QI-3: InfoCard tagged ucp-atomic ────────────────────────
const InfoCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="ucp-atomic" style={{ backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '18px 20px', marginBottom: '14px' }}>
    <p style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '1px', color: '#717182', marginBottom: '10px' }}>{title}</p>
    {children}
  </div>
);

const SEV_COLOR: Record<string, string> = {
  Critical: '#ef4444', High: '#f97316', Medium: '#f59e0b', Low: '#10b981',
};

// ── Component ────────────────────────────────────────────────
export default function EAPrintableUseCaseProfilePage() {
  const navigate = useNavigate();
  const search = useLocation().search;

  // WHICH use case this export is for. It arrives as ?id= because this page opens
  // in a new tab, where router state does not exist. Without it the page rendered
  // rank 1 no matter which use case you pressed Export on — so the document and
  // the button that brought you here disagreed about their own subject.
  const exportedId = readExportedUseCaseId(search);

  const { profile, clientName } = useMemo(() => {
    const sorted = [...DEMO_OPPORTUNITIES].sort(
      (a: any, b: any) => (a.rank ?? 999) - (b.rank ?? 999)
    );
    // Unknown or absent id falls back to the top-ranked use case, which keeps a
    // hand-typed /use-case/print rendering something rather than erroring.
    const index = exportedId ? sorted.findIndex((o: any) => o.id === exportedId) : -1;
    const resolved = index >= 0 ? index : 0;
    return {
      profile: buildProfile(sorted[resolved], resolved + 1),
      clientName: DEMO_CLIENT.name,
    };
  }, [exportedId]);

  // Back to the Explorer, on the Explorer tab, with this use case focused.
  const returnPath = resolveExportReturn(search);

  React.useEffect(() => {
    const prev = document.title;
    document.title = `DVA Use Case Profile – ${clientName} – ${formatDate()}`;
    return () => { document.title = prev; };
  }, [profile.title, clientName]);

  const recColor = profile.recommendation === 'PROCEED' ? '#10b981'
    : profile.recommendation === 'EVALUATE' ? '#f59e0b'
    : '#ef4444';

  const recLabel: Record<string, string> = {
    PROCEED:  'Recommended to Advance',
    EVALUATE: 'Recommended for Evaluation',
    DEFER:    'Recommended to Defer',
  };

  return (
    <>
      {/* ── Print Stylesheet ─────────────────────────────── */}
      <style>{`
        @media print {
          .ucp-no-print { display: none !important; }

          @page {
            size: letter;
            margin: 0.75in 0.65in;
          }

          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            background: white !important;
          }

          button { display: none !important; }
          select { display: none !important; }

          /* QI-4/QI-6: page 1 forces break */
          .ucp-page1 {
            page-break-after: always;
            break-after: always;
          }

          .ucp-section {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          .ucp-section-break {
            page-break-before: always;
            break-before: always;
          }

          .ucp-atomic {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          .ucp-section [class*="rounded-lg"],
          .ucp-section [class*="rounded-xl"],
          .ucp-section [class*="rounded-md"],
          .ucp-section [class*="rounded-"] {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          .ucp-section [style*="border-radius"] {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          .ucp-section [class*="grid"] > *,
          .ucp-section [class*="flex-col"] > *,
          .ucp-section [style*="display: grid"] > *,
          .ucp-section [style*="display: flex"] > * {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          h1, h2, h3, h4, h5 {
            break-after: avoid;
            page-break-after: avoid;
          }

          p {
            orphans: 3;
            widows: 3;
          }

          .ucp-watermark {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-35deg);
            font-size: 72px;
            font-weight: 800;
            color: rgba(24, 54, 94, 0.06);
            letter-spacing: 8px;
            white-space: nowrap;
            pointer-events: none;
            z-index: 0;
          }

          .ucp-content {
            position: relative;
            z-index: 1;
          }

          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }

        @media screen {
          .ucp-watermark {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-35deg);
            font-size: 72px;
            font-weight: 800;
            color: rgba(24, 54, 94, 0.06);
            letter-spacing: 8px;
            white-space: nowrap;
            pointer-events: none;
            z-index: 0;
          }
        }
      `}</style>

      {/* ── Watermark ────────────────────────────────────── */}
      <div className="ucp-watermark" aria-hidden="true">EARLY ADOPTER PREVIEW</div>

      {/* ── Toolbar (hidden in print) ─────────────────────── */}
      <div
        className="ucp-no-print sticky top-0 z-50 bg-white border-b border-[#e5e7eb] px-6 py-3 flex items-center justify-between"
        style={{ fontFamily: 'Montserrat, sans-serif' }}
      >
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(returnPath)}
            className="flex items-center gap-2 text-sm text-[#717182] hover:text-[#18365E] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Explorer
          </button>
          <div className="h-5 w-px bg-[#e5e7eb]" />
          <span className="text-sm font-semibold text-[#18365E]">
            Use Case Profile — Print Preview
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 px-4 py-2 bg-[#18365E] text-white text-sm font-semibold rounded-lg hover:bg-[#18365E]/90 transition-colors"
          >
            <Printer className="w-4 h-4" />
            Print / Save as PDF
          </button>
          <span className="text-xs text-[#717182]">
            Tip: In Print dialog, select "Save as PDF" for a digital copy
          </span>
        </div>
      </div>

      {/* ── Report Body ──────────────────────────────────── */}
      <div className="ucp-content" style={{ fontFamily: 'Montserrat, sans-serif' }}>

        {/* ════════════════════════════════════════════════ */}
        {/* QI-4 / QI-6: PAGE 1 — INITIATIVE DECISION PAGE */}
        {/* QI-6: All spacing tightened for single-page fit */}
        {/* ════════════════════════════════════════════════ */}
        <div className="ucp-page1 bg-white" style={{ padding: '24px 40px 16px 40px' }}>

          {/* ── Compact brand header ───────────────────────── */}
          <div style={{
            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
            marginBottom: '14px', paddingBottom: '12px', borderBottom: '3px solid #18365E',
            fontFamily: 'Montserrat, sans-serif',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <div style={{ width: '5px', height: '48px', backgroundColor: '#FF6E4C', borderRadius: '3px', flexShrink: 0, marginTop: '2px' }} />
              <div>
                <p style={{ fontSize: '11px', fontWeight: 700, color: '#717182', textTransform: 'uppercase', letterSpacing: '1.5px', margin: '0 0 4px 0' }}>
                  Use Case Profile · {profile.category}
                </p>
                <p style={{ fontSize: '20px', fontWeight: 800, color: '#18365E', margin: 0, lineHeight: 1.2, maxWidth: '480px' }}>
                  {profile.title}
                </p>
                <p style={{ fontSize: '12px', color: '#717182', margin: '4px 0 0 0' }}>
                  {clientName} · {profile.priority_label}
                </p>
              </div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <p style={{ fontSize: '11px', color: '#717182', margin: 0 }}>Adaptive Product · DVA Platform</p>
              <p style={{ fontSize: '11px', color: '#717182', margin: '2px 0 4px 0' }}>{formatDate()}</p>
              <span style={{ fontSize: '10px', fontWeight: 700, color: '#FF6E4C', textTransform: 'uppercase', letterSpacing: '1px' }}>
                Early Adopter Preview
              </span>
            </div>
          </div>

          {/* ── Recommendation banner ──────────────────────── */}
          <div className="ucp-atomic" style={{
            display: 'flex', alignItems: 'center', gap: '16px',
            padding: '12px 16px',
            backgroundColor: `${recColor}12`, border: `1.5px solid ${recColor}40`,
            borderRadius: '10px', marginBottom: '10px',
          }}>
            <span style={{
              backgroundColor: recColor, color: '#fff', fontSize: '11px', fontWeight: 800,
              padding: '5px 14px', borderRadius: '20px', letterSpacing: '1px', flexShrink: 0,
            }}>
              {recLabel[profile.recommendation] ?? profile.recommendation}
            </span>
            <p style={{ fontSize: '12px', lineHeight: 1.6, color: '#374151', margin: 0, flex: 1 }}>
              {profile.rationale}
            </p>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <p style={{ fontSize: '10px', color: '#717182', margin: 0, textTransform: 'uppercase', letterSpacing: '1px' }}>Portfolio Rank</p>
              <p style={{ fontSize: '18px', fontWeight: 800, color: '#18365E', margin: 0 }}>#{profile.rank}</p>
              <p style={{ fontSize: '11px', color: '#717182', margin: 0 }}>{profile.percentile}th pctile</p>
            </div>
          </div>

          {/* ── KPI strip ──────────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px', marginBottom: '10px' }}>
            {[
              { label: 'Composite Score',   value: `${profile.total_score}/100`,          sub: 'DVA overall rating',                    highlight: true  },
              { label: 'Business Value',    value: `${profile.value_score}/100`,           sub: 'Value dimension',                       highlight: false },
              { label: 'Impl. Feasibility', value: `${profile.implementation_score}/100`,  sub: 'Ease dimension',                        highlight: false },
              { label: 'ROI Range',         value: profile.roi_range,                      sub: `Annual benefit · base case, ${profile.time_to_roi}`, highlight: false },
              { label: 'Payback Period',    value: `${profile.roi.payback_months} months`, sub: 'To break-even (base case)',             highlight: false },
              { label: 'Model Confidence',  value: `${profile.roi.confidence}%`,           sub: 'Financial model confidence',            highlight: false },
            ].map(({ label, value, sub, highlight }) => (
              <div key={label} className="ucp-atomic" style={{
                padding: '8px 12px',
                backgroundColor: highlight ? '#18365E' : '#F0F4F8',
                borderRadius: '10px', border: highlight ? 'none' : '1px solid #e5e7eb',
              }}>
                <p style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: highlight ? '#FF6E4C' : '#717182', margin: '0 0 4px 0' }}>{label}</p>
                <p style={{ fontSize: '18px', fontWeight: 800, color: highlight ? '#ffffff' : '#18365E', margin: '0 0 2px 0', lineHeight: 1.1 }}>{value}</p>
                <p style={{ fontSize: '11px', color: highlight ? 'rgba(255,255,255,0.6)' : '#717182', margin: 0 }}>{sub}</p>
              </div>
            ))}
          </div>

          {/* ── Executive narrative ────────────────────────── */}
          <div style={{ marginBottom: '10px', padding: '10px 14px', backgroundColor: '#F8F9FB', borderLeft: '3px solid #FF6E4C', borderRadius: '0 8px 8px 0' }}>
            <p style={{ fontSize: '12px', lineHeight: 1.7, color: '#374151', margin: 0 }}>
              {profile.executive_summary}
            </p>
          </div>

          {/* ── 3 Reasons to Advance Now ────────────────────── */}
          <div style={{ marginBottom: '10px' }}>
            <p style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', color: '#18365E', margin: '0 0 7px 0' }}>
              3 Reasons to Advance Now
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {[
                `Strong financial profile: ${profile.roi_range} projected annual benefit with a ${profile.roi.payback_months}-month payback at ${profile.roi.confidence}% model confidence — structured for CFO review and capital allocation approval.`,
                `Portfolio priority: Ranked #${profile.rank} of ${TOTAL_PORTFOLIO_SIZE} evaluated initiatives (${profile.percentile}th percentile) — consistently scoring in the highest tier on both value and implementation dimensions.`,
                `Strategic timing: ${profile.category} domain investment is accelerating across peer health systems. Early movers are establishing competitive advantages and reusable infrastructure that compound over time.`,
              ].map((text, i) => (
                <div key={i} className="ucp-atomic" style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '7px 10px', backgroundColor: '#ffffff', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                  <span style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#FF6E4C', color: '#fff', fontSize: '10px', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>
                    {i + 1}
                  </span>
                  <p style={{ fontSize: '11px', lineHeight: 1.6, color: '#374151', margin: 0 }}>{text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Sponsor recommendation callout ──────────────── */}
          <div className="ucp-atomic" style={{ padding: '11px 16px', backgroundColor: '#18365E', borderRadius: '10px', color: '#ffffff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '24px' }}>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '11px', fontWeight: 700, color: '#FF6E4C', textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 4px 0' }}>
                Recommended Sponsors
              </p>
              <p style={{ fontSize: '12px', lineHeight: 1.6, color: 'rgba(255,255,255,0.85)', margin: 0 }}>
                <strong style={{ color: '#fff' }}>Executive:</strong> CDO / COO &nbsp;·&nbsp;
                <strong style={{ color: '#fff' }}>Finance:</strong> CFO (ROI validation) &nbsp;·&nbsp;
                <strong style={{ color: '#fff' }}>Clinical:</strong> CMO (workflow integration)
              </p>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', margin: '0 0 3px 0', textTransform: 'uppercase', letterSpacing: '1px' }}>Engagement Type</p>
              <p style={{ fontSize: '13px', fontWeight: 700, color: '#ffffff', margin: '0 0 2px 0' }}>Decision Framework Implementation</p>
              <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)', margin: 0 }}>Scope · Commit · Activate</p>
            </div>
          </div>

        </div>
        {/* ── End page 1 ───────────────────────────────────── */}

        {/* ════════════════════════════════════════════════ */}
        {/* QI-6: PAGE 2 — HOW TO USE THIS DECISION PROFILE */}
        {/* Role-based navigation (CFO/CDO/COO) + key       */}
        {/* metrics summary. Replaces QI-5 section-card nav.*/}
        {/* ════════════════════════════════════════════════ */}
        <div className="ucp-section-break bg-white" style={{ padding: '40px 40px 36px 40px', fontFamily: 'Montserrat, sans-serif' }}>

          {/* ── Page 2 header ─────────────────────────────── */}
          <div style={{ marginBottom: '24px', paddingBottom: '16px', borderBottom: '3px solid #18365E' }}>
            <p style={{ fontSize: '11px', fontWeight: 700, color: '#FF6E4C', textTransform: 'uppercase', letterSpacing: '2px', margin: '0 0 6px 0' }}>
              Use Case Profile · {profile.category}
            </p>
            <h2 style={{ fontSize: '22px', fontWeight: 800, color: '#18365E', margin: '0 0 4px 0', fontFamily: 'Montserrat, sans-serif' }}>
              How to Use This Decision Profile
            </h2>
            <p style={{ fontSize: '13px', color: '#717182', margin: 0 }}>
              A structured guide for executive review and sponsor action — organized by role and decision stage.
            </p>
          </div>

          {/* ── What this document provides ───────────────── */}
          <div style={{ marginBottom: '24px' }}>
            <p style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#18365E', margin: '0 0 10px 0' }}>
              What This Document Provides
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {[
                { num: '01', label: 'Strategic Alignment & Context',  desc: 'Initiative description, domain context, and sponsor alignment' },
                { num: '02', label: 'Financial Model & ROI Detail',    desc: `Annual benefit, payback, NPV, and key assumptions at ${profile.roi.confidence}% confidence` },
                { num: '03', label: 'Implementation Feasibility',      desc: 'Technology, data, organizational, and integration readiness' },
                { num: '04', label: 'Risk & Mitigation Profile',       desc: 'Risk register with owners, mitigations, and likelihood ratings' },
                { num: '05', label: 'Execution Roadmap',               desc: `${profile.milestones.length}-phase timeline from discovery through enterprise deployment` },
                { num: '06', label: 'Recommended Actions',             desc: 'Immediate, near-term, and strategic next steps with owners assigned' },
              ].map(({ num, label, desc }) => (
                <div key={num} className="ucp-atomic" style={{
                  display: 'flex', gap: '10px', padding: '10px 12px',
                  backgroundColor: '#F8F9FB', border: '1px solid #e5e7eb', borderRadius: '8px',
                }}>
                  <span style={{ fontSize: '12px', fontWeight: 800, color: '#FF6E4C', flexShrink: 0, width: '24px' }}>{num}</span>
                  <div>
                    <p style={{ fontSize: '12px', fontWeight: 700, color: '#18365E', margin: '0 0 2px 0' }}>{label}</p>
                    <p style={{ fontSize: '11px', color: '#717182', margin: 0 }}>{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Navigate by role ──────────────────────────── */}
          <div style={{ marginBottom: '24px' }}>
            <p style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#18365E', margin: '0 0 10px 0' }}>
              Navigate by Role
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
              {[
                {
                  role: 'CFO / Finance',
                  focus: 'Investment approval and ROI validation',
                  sections: ['02 — Financial Model & ROI Detail', '04 — Risk & Mitigation Profile', '06 — Recommended Actions'],
                  accent: '#FF6E4C',
                },
                {
                  role: 'CDO / VP Analytics',
                  focus: 'Strategic fit and delivery ownership',
                  sections: ['01 — Strategic Alignment & Context', '03 — Implementation Feasibility', '05 — Execution Roadmap'],
                  accent: '#18365E',
                },
                {
                  role: 'COO / Clinical',
                  focus: 'Operational impact and change readiness',
                  sections: ['01 — Strategic Alignment & Context', '03 — Implementation Feasibility', '06 — Recommended Actions'],
                  accent: '#717182',
                },
              ].map(({ role, focus, sections, accent }) => (
                <div key={role} className="ucp-atomic" style={{
                  padding: '14px', backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb', borderTop: `3px solid ${accent}`, borderRadius: '8px',
                }}>
                  <p style={{ fontSize: '13px', fontWeight: 800, color: '#18365E', margin: '0 0 3px 0' }}>{role}</p>
                  <p style={{ fontSize: '11px', color: '#717182', margin: '0 0 10px 0', fontStyle: 'italic' }}>{focus}</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    {sections.map((s) => (
                      <p key={s} style={{ fontSize: '11px', color: '#374151', margin: 0, paddingLeft: '8px', borderLeft: `2px solid ${accent}50` }}>
                        {s}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Key metrics at a glance ───────────────────── */}
          <div style={{ marginBottom: '20px' }}>
            <p style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#18365E', margin: '0 0 10px 0' }}>
              Key Metrics at a Glance
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '8px' }}>
              {[
                { label: 'Composite Score', value: `${profile.total_score}/100`            },
                { label: 'Portfolio Rank',  value: `#${profile.rank}`                      },
                { label: 'ROI Range',       value: profile.roi_range                       },
                { label: 'Payback Period',  value: `${profile.roi.payback_months}mo`        },
                { label: '3-Year NPV',      value: `$${profile.roi.npv_3yr_m.toFixed(1)}M` },
                { label: 'Confidence',      value: `${profile.roi.confidence}%`            },
              ].map(({ label, value }) => (
                <div key={label} className="ucp-atomic" style={{
                  textAlign: 'center', padding: '10px 8px',
                  backgroundColor: '#F0F4F8', borderRadius: '8px', border: '1px solid #e5e7eb',
                }}>
                  <p style={{ fontSize: '16px', fontWeight: 800, color: '#18365E', margin: '0 0 3px 0' }}>{value}</p>
                  <p style={{ fontSize: '10px', color: '#717182', margin: 0, textTransform: 'uppercase' as const, letterSpacing: '0.5px' }}>{label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Cross-artifact reference ───────────────────── */}
          <div style={{ padding: '12px 16px', backgroundColor: '#F8F9FB', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
            <p style={{ fontSize: '11px', color: '#717182', lineHeight: 1.65, margin: 0 }}>
              <strong style={{ color: '#18365E' }}>Companion artifacts:</strong>{' '}
              This initiative was surfaced through the <strong style={{ color: '#18365E' }}>Portfolio Strategy Report</strong>.
              Execution planning and governance are in the <strong style={{ color: '#18365E' }}>Strategic Execution Plan</strong>.
              Finance-ready documentation appears in the <strong style={{ color: '#18365E' }}>CFO ROI Summary</strong> and{' '}
              <strong style={{ color: '#18365E' }}>Board Rationale</strong>.
            </p>
          </div>

        </div>
        {/* ── End page 2 ───────────────────────────────────── */}

        {/* ══════════════════════════════════════════════════ */}
        {/* DETAILED SECTIONS (pages 3+)                       */}
        {/* QI-6: Prose converted to bullets via toBullets()   */}
        {/* ══════════════════════════════════════════════════ */}
        <div className="bg-white" style={{ padding: '32px 40px' }}>

          {/* ── 1. Strategic Alignment & Context ─────────── */}
          <div className="ucp-section ucp-section-break">
            <SectionHeader title="Strategic Alignment & Context" subtitle="Initiative description, strategic context, and sponsor alignment" />

            <InfoCard title="Initiative Description">
              <p style={{ fontSize: '13px', lineHeight: 1.7, color: '#374151' }}>{profile.use_case_description}</p>
            </InfoCard>

            <InfoCard title="Strategic Narrative">
              {toBullets(profile.strategic_narrative)}
            </InfoCard>

            <InfoCard title="Strategic Context">
              {toBullets(profile.strategic_context)}
            </InfoCard>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <InfoCard title="Operational Impact">
                {toBullets(profile.operational_impact)}
              </InfoCard>
              <InfoCard title="Clinical Impact">
                {toBullets(profile.clinical_impact)}
              </InfoCard>
            </div>

            <InfoCard title="Organizational Impact">
              {toBullets(profile.organizational_impact)}
            </InfoCard>

            <InfoCard title="Sponsor Alignment">
              {toBullets(profile.stakeholder_alignment)}
            </InfoCard>
          </div>

          {/* ── 2. Financial Model & ROI Detail ──────────── */}
          <div className="ucp-section ucp-section-break">
            <SectionHeader title="Financial Model & ROI Detail" subtitle="Financial model summary and key assumptions" />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '14px', marginBottom: '20px' }}>
              <div className="ucp-atomic" style={{ backgroundColor: '#18365E', borderRadius: '12px', padding: '20px', color: '#fff' }}>
                <p style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: 'rgba(255,255,255,0.6)', marginBottom: '8px' }}>Annual Benefit</p>
                <p style={{ fontSize: '28px', fontWeight: 800 }}>${profile.roi.annual_benefit_m.toFixed(1)}M</p>
                <p style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '4px' }}>projected annually</p>
              </div>
              {[
                { label: 'Implementation Cost', value: `$${profile.roi.implementation_cost_m.toFixed(1)}M`, sub: 'total investment'   },
                { label: 'Payback Period',       value: `${profile.roi.payback_months} months`,             sub: 'to break-even'     },
                { label: '3-Year NPV',           value: `$${profile.roi.npv_3yr_m.toFixed(1)}M`,            sub: 'net present value' },
              ].map(({ label, value, sub }) => (
                <div key={label} className="ucp-atomic" style={{ backgroundColor: '#F0F4F8', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px' }}>
                  <p style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#717182', marginBottom: '8px' }}>{label}</p>
                  <p style={{ fontSize: '22px', fontWeight: 800, color: '#18365E' }}>{value}</p>
                  <p style={{ fontSize: '11px', color: '#717182', marginTop: '4px' }}>{sub}</p>
                </div>
              ))}
            </div>

            <InfoCard title={`Financial Narrative · ${profile.roi.confidence}% Confidence`}>
              <p style={{ fontSize: '12px', lineHeight: 1.7, color: '#374151' }}>{profile.roi_narrative}</p>
              <p style={{ fontSize: '11px', color: '#717182', fontStyle: 'italic', marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #e5e7eb', marginBottom: 0 }}>
                Detailed financial model inputs and benchmark sources are documented in the Assumptions Appendix.
                A finance-ready investment brief is available in the CFO ROI Summary.
                Governance documentation for board submission appears in the Board Rationale.
              </p>
            </InfoCard>

            <InfoCard title="Key Assumptions">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                {profile.roi_assumptions.map((a, i) => (
                  <div key={i} className="ucp-atomic" style={{ display: 'flex', gap: '10px', backgroundColor: '#F5F7FA', borderRadius: '8px', padding: '10px', border: '1px solid #e5e7eb' }}>
                    <span style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: '#FF6E4C', color: '#fff', fontSize: '10px', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {i + 1}
                    </span>
                    <div>
                      <p style={{ fontSize: '11px', fontWeight: 700, color: '#18365E', margin: 0 }}>{a.label}</p>
                      <p style={{ fontSize: '12px', color: '#717182', margin: 0 }}>{a.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </InfoCard>
          </div>

          {/* ── 3. Implementation Feasibility ────────────── */}
          <div className="ucp-section ucp-section-break">
            <SectionHeader title="Implementation Feasibility" subtitle="Complexity assessment and readiness evaluation" />

            <div className="ucp-atomic" style={{
              backgroundColor: profile.implementation_effort === 'Low' ? '#f0fdf4' : profile.implementation_effort === 'Medium' ? '#fffbeb' : '#fef2f2',
              border: `1px solid ${profile.implementation_effort === 'Low' ? '#bbf7d0' : profile.implementation_effort === 'Medium' ? '#fde68a' : '#fecaca'}`,
              borderRadius: '12px', padding: '20px', marginBottom: '20px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <p style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#717182', marginBottom: '6px' }}>Implementation Complexity</p>
                <p style={{ fontSize: '20px', fontWeight: 800, color: '#18365E', marginBottom: '4px' }}>
                  {profile.implementation_effort} Effort · Score {profile.implementation_score}/100
                </p>
                <p style={{ fontSize: '13px', lineHeight: 1.6, color: '#374151', maxWidth: '480px' }}>{profile.feasibility.complexity_summary}</p>
              </div>
              <div style={{ textAlign: 'center', flexShrink: 0 }}>
                <p style={{ fontSize: '40px', fontWeight: 800, color: '#18365E' }}>{profile.implementation_score}</p>
                <p style={{ fontSize: '11px', color: '#717182' }}>/ 100</p>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
              {[
                { title: 'Technology Readiness',    body: profile.feasibility.technology_readiness     },
                { title: 'Data Availability',        body: profile.feasibility.data_availability        },
                { title: 'Organizational Readiness', body: profile.feasibility.organizational_readiness },
                { title: 'Integration Complexity',   body: profile.feasibility.integration_complexity   },
                { title: 'Operational Dependencies', body: profile.feasibility.operational_dependencies },
                { title: 'Change Management',        body: profile.feasibility.change_management        },
              ].map(({ title, body }) => (
                <InfoCard key={title} title={title}>
                  <p style={{ fontSize: '12px', lineHeight: 1.65, color: '#374151' }}>{body}</p>
                </InfoCard>
              ))}
            </div>
          </div>

          {/* ── 4. Risk & Mitigation Profile ─────────────── */}
          <div className="ucp-section ucp-section-break">
            <SectionHeader title="Risk & Mitigation Profile" subtitle="Key implementation and strategic risks with mitigations" />

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {profile.risks.map((risk, i) => (
                <div key={i} className="ucp-atomic" style={{
                  backgroundColor: '#ffffff', borderRadius: '10px', border: `1px solid #e5e7eb`,
                  borderLeft: `4px solid ${SEV_COLOR[risk.severity] || '#717182'}`, padding: '16px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <p style={{ fontSize: '14px', fontWeight: 700, color: '#18365E', margin: 0 }}>{risk.name}</p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <span style={{ fontSize: '11px', fontWeight: 700, padding: '2px 10px', borderRadius: '12px', backgroundColor: `${SEV_COLOR[risk.severity]}20`, color: SEV_COLOR[risk.severity] }}>{risk.severity}</span>
                      <span style={{ fontSize: '11px', color: '#717182', padding: '2px 8px', borderRadius: '12px', border: '1px solid #e5e7eb' }}>{risk.likelihood} likelihood</span>
                    </div>
                  </div>
                  <p style={{ fontSize: '12px', lineHeight: 1.6, color: '#374151', marginBottom: '8px' }}>{risk.description}</p>
                  <div style={{ backgroundColor: '#F5F7FA', borderRadius: '6px', padding: '10px' }}>
                    <p style={{ fontSize: '11px', fontWeight: 700, color: '#18365E', marginBottom: '4px' }}>Mitigation</p>
                    <p style={{ fontSize: '12px', color: '#717182', margin: 0 }}>{risk.mitigation}</p>
                  </div>
                  <p style={{ fontSize: '11px', color: '#717182', marginTop: '6px' }}>Owner: <span style={{ fontWeight: 600 }}>{risk.owner}</span></p>
                </div>
              ))}
            </div>
          </div>

          {/* ── 5. Execution Roadmap ─────────────────────── */}
          <div className="ucp-section ucp-section-break">
            <SectionHeader title="Execution Roadmap" subtitle="Phased delivery timeline and milestone overview" />

            <div className="ucp-atomic" style={{ backgroundColor: '#18365E', borderRadius: '12px', padding: '24px', marginBottom: '20px', color: '#fff' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px' }}>
                {[
                  { label: 'Estimated Deployment', value: '12 months', sub: 'to full enterprise rollout' },
                  { label: 'Time to First Value',  value: '6 months',  sub: 'pilot go-live milestone'    },
                  { label: 'Total Milestones',     value: `${profile.milestones.length}`, sub: 'implementation phases' },
                ].map(({ label, value, sub }) => (
                  <div key={label}>
                    <p style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: 'rgba(255,255,255,0.5)', marginBottom: '4px' }}>{label}</p>
                    <p style={{ fontSize: '28px', fontWeight: 800 }}>{value}</p>
                    <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>{sub}</p>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {profile.milestones.map((m, i) => (
                <div key={i} className="ucp-atomic" style={{ display: 'flex', gap: '16px', backgroundColor: '#F8F9FB', borderRadius: '10px', padding: '16px', border: '1px solid #e5e7eb' }}>
                  <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#18365E', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 800, flexShrink: 0 }}>
                    M{m.months_from_start}
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '14px', fontWeight: 700, color: '#18365E', margin: '0 0 4px 0' }}>{m.label}</p>
                    <p style={{ fontSize: '12px', lineHeight: 1.6, color: '#374151', marginBottom: '6px' }}>{m.value_note}</p>
                    <p style={{ fontSize: '11px', color: '#717182' }}>
                      <span style={{ fontWeight: 600 }}>Target outcome:</span> {m.expected_outcome}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── 6. Recommended Actions ───────────────────── */}
          <div className="ucp-section ucp-section-break">
            <SectionHeader title="Recommended Actions" subtitle="Prioritized next steps for initiative launch" />

            {[
              { group: 'Immediate Actions', items: profile.actions.immediate, accent: '#FF6E4C' },
              { group: 'Near-Term Actions', items: profile.actions.near_term,  accent: '#18365E' },
              { group: 'Strategic Actions', items: profile.actions.strategic,  accent: '#717182' },
            ].map(({ group, items, accent }) => (
              <div key={group} className="ucp-atomic" style={{ marginBottom: '20px' }}>
                <p style={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: accent, marginBottom: '10px' }}>{group}</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {items.map((action, i) => (
                    <div key={i} className="ucp-atomic" style={{ backgroundColor: '#F8F9FB', borderRadius: '10px', padding: '16px', border: '1px solid #e5e7eb', borderLeft: `4px solid ${accent}` }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                        <p style={{ fontSize: '14px', fontWeight: 700, color: '#18365E', margin: 0 }}>{action.title}</p>
                        <span style={{ fontSize: '11px', color: '#717182', fontWeight: 600, flexShrink: 0 }}>{action.owner}</span>
                      </div>
                      <p style={{ fontSize: '12px', lineHeight: 1.6, color: '#374151', margin: 0 }}>{action.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* ── Report Footer ────────────────────────────── */}
          <div className="mt-12 pt-6 border-t border-[#e5e7eb] flex items-center justify-between">
            <div>
              <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#717182]">Use Case Profile</p>
              <p className="text-[10px] text-[#FF6E4C] font-semibold">DVA · Adaptive Product</p>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-[#717182]">{clientName} · {formatDate()}</p>
              <p className="text-[10px] font-semibold tracking-[0.1em] uppercase text-[#FF6E4C]">Early Adopter Preview</p>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
